import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientService } from '../../services/clientService';
import { Client } from '../../types/client.types';
import { useToast } from '../../context/ToastContext';
import ConfirmModal from '../../components/common/ConfirmModal';
import AdminHeader from '../../components/admin/AdminHeader';

const ClientManagement: React.FC = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [clients, setClients] = useState<Client[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [clientToDelete, setClientToDelete] = useState<number | null>(null);

    useEffect(() => {
        loadClients();
    }, []);

    const loadClients = async (search?: string) => {
        try {
            if (search) setIsSearching(true);
            else setLoading(true);
            
            const data = await clientService.getAllClientsAdmin(search);
            setClients(data || []);
            setError(null);
        } catch (err) {
            console.error('Error loading clients:', err);
            setError('No se pudieron cargar los clientes. Por favor, intenta de nuevo.');
            showToast('Error al cargar la lista de clientes', 'error');
        } finally {
            setLoading(false);
            setIsSearching(false);
        }
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        loadClients(searchQuery);
    };

    const handleWhatsAppClick = (phone?: string) => {
        if (!phone) {
            showToast('Este cliente no tiene WhatsApp configurado', 'warning');
            return;
        }
        const cleanPhone = phone.replace(/\D/g, '');
        window.open(`https://wa.me/${cleanPhone}`, '_blank');
    };

    const handleDeleteClick = (clientId: number) => {
        setClientToDelete(clientId);
        setShowConfirmModal(true);
    };

    const confirmDelete = async () => {
        if (clientToDelete === null) return;
        setShowConfirmModal(false);

        try {
            await clientService.deleteClientAdmin(clientToDelete);
            showToast('Cliente eliminado exitosamente', 'success');
            loadClients(searchQuery); // Reload with current search
        } catch (error) {
            console.error('Error deleting client:', error);
            showToast('Error al eliminar el cliente', 'error');
        } finally {
            setClientToDelete(null);
        }
    };

    return (
        <>
            <AdminHeader
                title="👥 Gestión de Clientes"
                rightContent={
                    <button className="btn btn-primary btn-sm" onClick={() => navigate('/admin/create-client')}>
                        ➕ Agregar Cliente
                    </button>
                }
            />
            <div className="container fade-in">
                {/* Search Bar */}
                <form onSubmit={handleSearch} style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.5rem' }}>
                    <input
                        type="text"
                        placeholder="Buscar por nombre, apellido, email o teléfono..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                            flex: 1,
                            padding: '0.75rem',
                            borderRadius: '0.5rem',
                            border: '1px solid var(--secondary)',
                            backgroundColor: 'var(--surface)',
                            color: 'var(--text-primary)',
                            fontSize: '1rem'
                        }}
                    />
                    <button type="submit" className="btn btn-primary" disabled={isSearching}>
                        {isSearching ? 'Buscando...' : '🔍 Buscar'}
                    </button>
                    {searchQuery && (
                        <button type="button" className="btn btn-outline" onClick={() => { setSearchQuery(''); loadClients(); }}>
                            ❌ Limpiar
                        </button>
                    )}
                </form>

                {loading ? (
                    <div className="card p-xl text-center">
                        <div className="spinner m-auto mb-md"></div>
                        <p>Cargando clientes...</p>
                    </div>
                ) : error ? (
                    <div className="card p-xl text-center border-error bg-error-light">
                        <p className="text-error mb-md">{error}</p>
                        <button className="btn btn-outline btn-sm" onClick={() => loadClients(searchQuery)}>Reintentar</button>
                    </div>
                ) : (clients && clients.length === 0) ? (
                    <div className="card p-xl text-center">
                        <p className="text-secondary mb-md">No se encontraron clientes.</p>
                        {!searchQuery && (
                            <button className="btn btn-primary" onClick={() => navigate('/admin/create-client')}>
                                Agregar primer cliente
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="responsive-grid">
                            {clients.map((client) => (
                                <div key={client.id} className="card p-md shadow-sm hover:shadow-md transition-all flex flex-col" style={{
                                    background: '#111111',
                                    borderRadius: '1rem',
                                    border: '1px solid #2a2a2a',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                                }}>
                                    {/* Status badges */}
                                    <div className="flex justify-end gap-sm mb-sm">
                                        <span style={{
                                            fontSize: '0.6rem',
                                            padding: '0.25rem 0.6rem',
                                            borderRadius: '1rem',
                                            fontWeight: '700',
                                            background: '#3b82f620',
                                            color: '#3b82f6',
                                            border: '1px solid #3b82f640'
                                        }}>
                                            {client.totalAppointments} TURNOS
                                        </span>
                                    </div>

                                    {/* Info */}
                                    <div className="flex flex-col mb-md">
                                        <h3 className="m-0 font-semibold text-lg mb-xs" style={{ color: '#e5e5e5' }}>
                                            {client.firstName} {client.lastName}
                                        </h3>
                                        <p className="text-sm m-0 mb-xs" style={{ color: '#a3a3a3' }}>
                                            📧 {client.email}
                                        </p>
                                        <p className="text-sm m-0 mb-sm" style={{ color: '#737373' }}>
                                            📞 {client.phoneNumber || 'Sin teléfono'}
                                        </p>

                                        {/* WhatsApp Button */}
                                        {client.phoneNumber && (
                                            <button
                                                className="btn btn-sm btn-outline gap-xs"
                                                style={{
                                                    backgroundColor: '#25D366',
                                                    color: 'white',
                                                    border: 'none',
                                                    fontSize: '0.75rem',
                                                    padding: '0.4rem 0.8rem',
                                                    marginTop: '0.5rem',
                                                    width: 'fit-content'
                                                }}
                                                onClick={() => handleWhatsAppClick(client.phoneNumber)}
                                            >
                                                💬 WhatsApp
                                            </button>
                                        )}
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="grid grid-cols-2 gap-sm mt-auto">
                                        <button
                                            className="btn btn-sm font-bold"
                                            style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', color: '#000', borderRadius: '0.5rem', fontSize: '0.75rem' }}
                                            onClick={() => navigate(`/admin/edit-client/${client.id}`)}
                                        >
                                            ✏️ Editar
                                        </button>
                                        <button
                                            className="btn btn-sm font-bold"
                                            style={{ background: 'transparent', border: '1px solid #ef4444', color: '#ef4444', borderRadius: '0.5rem', fontSize: '0.75rem' }}
                                            onClick={() => handleDeleteClick(client.id)}
                                            title="Eliminar Cliente"
                                        >
                                            🗑️ Eliminar
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="text-center text-sm text-secondary mt-xl pb-3xl opacity-50 font-medium">
                            Se muestran {clients.length} cliente{clients.length !== 1 ? 's' : ''}
                        </div>
                    </>
                )}

                <ConfirmModal
                    isOpen={showConfirmModal}
                    title="Confirmar Eliminación"
                    message="¿Estás seguro de que deseas eliminar este cliente? Esta acción desactivará el usuario pero mantendrá sus turnos históricos por cuestiones contables."
                    onConfirm={confirmDelete}
                    onCancel={() => setShowConfirmModal(false)}
                    confirmText="Sí, Eliminar"
                    cancelText="Cancelar"
                />
            </div>
        </>
    );
};

export default ClientManagement;
